/// <reference types="vite/client" />
import * as Comlink from 'comlink'
import GeoWorker from './geo-compute.worker.ts?worker'
import type { GeoComputeWorkerApi } from './geo-worker-exposed-api.js'

let worker: Worker | undefined
let proxy: Comlink.Remote<GeoComputeWorkerApi> | undefined
let workerUnavailable = false

const GEO_VERBOSE =
  import.meta.env.DEV && String(import.meta.env.VITE_GPX_GEO_VERBOSE ?? '').toLowerCase() === 'true'

function logVerbose(method: string, where: 'worker' | 'main', detail?: string) {
  if (!GEO_VERBOSE) return
  console.debug(`[gpx-geo] ${method} → ${where}`, detail ?? '')
}

function warnFallback(method: string, reason: string) {
  if (import.meta.env.DEV) {
    console.warn(`[gpx-geo] ${method} → main (fallback)`, reason)
  }
}

export type RunInGeoWorkerOptions<TPayload, TResult> = {
  /** Key on {@link GeoComputeWorkerApi} (must exist on the worker `expose` object). */
  method: keyof GeoComputeWorkerApi
  payload: TPayload
  /** Structured-clone-safe copy for postMessage; omit if `payload` is already plain data. */
  cloneForWorker?: (payload: TPayload) => TPayload
  /** Same logic as in the worker, on the main thread (fallback + environments without Worker). */
  onMainThread: (payload: TPayload) => TResult
  /** Optional dev log suffix after a successful worker call. */
  debugDetail?: string
}

/**
 * Single place for Worker + Comlink + fallback. Domain functions only pass method name, payload, clone, and sync impl.
 */
export async function runInGeoWorker<TPayload, TResult>(
  options: RunInGeoWorkerOptions<TPayload, TResult>
): Promise<TResult> {
  const { method, payload, cloneForWorker, onMainThread, debugDetail } = options
  const methodName = String(method)
  const toWorker = cloneForWorker ? cloneForWorker(payload) : payload

  if (workerUnavailable || typeof Worker === 'undefined') {
    if (typeof Worker === 'undefined') {
      warnFallback(methodName, 'Worker API missing')
    } else {
      logVerbose(methodName, 'main', 'worker disabled after error')
    }
    return onMainThread(payload)
  }

  try {
    if (!worker || !proxy) {
      worker = new GeoWorker()
      proxy = Comlink.wrap<GeoComputeWorkerApi>(worker)
    }
    const remote = proxy[method] as unknown as (p: unknown) => Promise<TResult>
    const out = await remote(toWorker)
    logVerbose(methodName, 'worker', debugDetail ?? '')
    return out
  } catch (e) {
    workerUnavailable = true
    warnFallback(methodName, e instanceof Error ? e.message : String(e))
    try {
      worker?.terminate()
    } catch {
      /* ignore */
    }
    worker = undefined
    proxy = undefined
    return onMainThread(payload)
  }
}
